import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import { Box, Button, Grid, Input } from '@mui/material'
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded'
import Header from '../../components/Headers/Header'
import CustomLoader from '../../components/Loader/CustomLoader'
import { SectionCard, raisedButtonSx } from '../../components/UI/Depth'
import { influencerSidebarIcons } from '../../components/Sidebar/sidebarIcons'
import { getInfluencerDashboard, updateInfluencerProfile } from '../../apollo'
import useGlobalStyles from '../../utils/globalStyles'
import { getViewingInfluencerId } from '../../utils/influencerContext'
import { FieldLabel, FormFeedback, StatusPill } from './shared'

const GET_DASHBOARD = gql`
  ${getInfluencerDashboard}
`
const UPDATE_PROFILE = gql`
  ${updateInfluencerProfile}
`

const emptyBank = {
  accountHolderName: '',
  bankName: '',
  accountNumber: '',
  sortCode: ''
}

const fields = [
  { key: 'accountHolderName', labelKey: 'AccountHolderName' },
  { key: 'bankName', labelKey: 'BankName' },
  { key: 'accountNumber', labelKey: 'AccountNumber' },
  { key: 'sortCode', labelKey: 'SortCode' }
]

// A withdrawal is refused server-side without these, so this page is the
// only thing standing between "no bank details" and "money with nowhere to
// go" -- the same gap that showed up on a manually-credited wallet.
function InfluencerBankManagement(props) {
  const { t } = props
  const globalClasses = useGlobalStyles()
  const [bank, setBank] = useState(emptyBank)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const influencerId = getViewingInfluencerId()
  const { data, loading, refetch } = useQuery(GET_DASHBOARD, {
    variables: { influencerId },
    fetchPolicy: 'network-only'
  })

  const influencer = data?.influencerDashboard?.influencer

  useEffect(() => {
    if (!influencer) return
    const details = influencer.bankDetails || {}
    setBank({
      accountHolderName: details.accountHolderName || '',
      bankName: details.bankName || '',
      accountNumber: details.accountNumber || '',
      sortCode: details.sortCode || ''
    })
  }, [influencer])

  const [mutate, { loading: saving }] = useMutation(UPDATE_PROFILE, {
    onCompleted: () => {
      setError('')
      setSuccess(t('BankDetailsUpdated'))
      refetch()
      setTimeout(() => setSuccess(''), 4000)
    },
    onError: err => {
      setSuccess('')
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const isPayable = Boolean(bank.accountNumber.trim() && bank.sortCode.trim())

  const onSubmit = event => {
    event.preventDefault()
    if (!bank.accountNumber.trim() || !bank.sortCode.trim()) {
      setError(t('BankDetailsRequired'))
      return
    }
    mutate({
      variables: {
        influencerId,
        bankDetails: {
          accountHolderName: bank.accountHolderName.trim(),
          bankName: bank.bankName.trim(),
          accountNumber: bank.accountNumber.trim(),
          sortCode: bank.sortCode.trim()
        }
      }
    })
  }

  return (
    <>
      <Header
        title={t('Bank Management')}
        subtitle={t('SubtitleInfluencerBank')}
        icon={influencerSidebarIcons['Bank Management']}
      />
      {loading && !influencer ? (
        <CustomLoader />
      ) : (
        <SectionCard
          icon={AccountBalanceOutlined}
          tone="green"
          title={t('BankDetails')}
          subtitle={t('BankDetailsHelp')}
          action={
            isPayable ? (
              <StatusPill tone="green" icon={CheckCircleRounded}>
                {t('ReadyForPayouts')}
              </StatusPill>
            ) : (
              <StatusPill tone="rose" icon={ErrorOutlineRounded}>
                {t('NotSetUp')}
              </StatusPill>
            )
          }
          sx={{ maxWidth: 820 }}
          bodySx={{ pt: 2.5 }}>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2}>
              {fields.map(field => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <FieldLabel>{t(field.labelKey)}</FieldLabel>
                  <Input
                    id={`input-${field.key}`}
                    name={`input-${field.key}`}
                    placeholder={
                      field.key === 'sortCode'
                        ? t('SortCodePlaceholder')
                        : t(field.labelKey)
                    }
                    value={bank[field.key]}
                    onChange={e =>
                      setBank({ ...bank, [field.key]: e.target.value })
                    }
                    disableUnderline
                    className={globalClasses.input}
                  />
                </Grid>
              ))}
            </Grid>
            <Box mt={3}>
              <Button type="submit" disabled={saving} sx={raisedButtonSx}>
                {t('Save')}
              </Button>
            </Box>
          </form>
          <FormFeedback success={success} error={error} />
        </SectionCard>
      )}
    </>
  )
}

export default withTranslation()(InfluencerBankManagement)
